import { outOfBandThemeColors } from '@/theme/colors';
import CopyToClipboard from '@montugroup/circuit-shared/components/common/CopyToClipboard';
import { Box, Radio, Stack, Typography } from '@montugroup/design-system';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { styled } from '@mui/material';
import { ShippingOptionAlertPartialStock } from '../ShippingOptionCard/ShippingOptionAlertPartialStock';

export interface PharmacyCardProps {
  id: number;
  name: string;
  distanceKm?: number;
  address: string;
  openHours?: string;
  selected?: boolean;
  showRadio?: boolean;
  hasInventory?: boolean;
  onClick: () => void;
}

const ShippingOptionContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'hasError',
})<{ selected?: boolean; hasError?: boolean }>(({ theme, selected, hasError }) => ({
  border: selected ? '2px solid' : '1px solid',
  borderRadius: theme.shape.borderRadius,
  borderColor: hasError
    ? theme.palette.error.main
    : selected
      ? theme.palette.text.primary
      : theme.palette.action.disabled,
  backgroundColor: selected ? outOfBandThemeColors.uMeds.timberGreen100 : theme.palette.background.paper,
}));

export const ClickAndCollectPharmacyCard = ({
  name,
  distanceKm,
  address,
  openHours,
  selected = false,
  showRadio = true,
  hasInventory,
  onClick,
}: PharmacyCardProps) => {
  const [addressLine1, ...rest] = address.split(',');
  const addressLine2 = rest.join(',').trim();
  return (
    <ShippingOptionContainer selected={selected}>
      <Box onClick={onClick} data-testid="pharmacy-card">
        <Stack>
          <Stack direction="row" alignItems="flex-start">
            {showRadio && (
              <Box display="flex" alignItems="center" mr={-1}>
                <Radio checked={selected} />
              </Box>
            )}

            <Stack flex={1} padding={2} spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={600}>{name}</Typography>
                {distanceKm && (
                  <Typography fontWeight={700} variant="body2">
                    {distanceKm} km
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                <Stack direction="column" spacing={0}>
                  <Box display="flex" alignItems="flex-start">
                    <LocationOnIcon
                      data-testid="location-icon"
                      fontSize="small"
                      color="primary"
                      sx={{ position: 'relative', left: '-1rem', mt: '0.125rem' }}
                    />
                    <Stack direction="column" spacing={0} ml={-2}>
                      <Typography variant="body2" fontWeight={700}>
                        {addressLine1}
                      </Typography>
                      <Typography variant="body2">{addressLine2}</Typography>
                    </Stack>
                  </Box>
                </Stack>

                <CopyToClipboard textToCopy={address} buttonVariant="icon" size="small" sx={{ fontWeight: 700 }} />
              </Stack>

              {openHours && (
                <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line' }}>
                  <strong>Open hours</strong> {openHours}
                </Typography>
              )}
            </Stack>
          </Stack>

          <ShippingOptionAlertPartialStock hasStock={!!hasInventory} />
        </Stack>
      </Box>
    </ShippingOptionContainer>
  );
};
